import React from 'react'
import { useTranslation } from 'react-i18next'
import { INVENTORY_MULTI_SELECTION_UPDATES, INVENTORY_ZOOM_LEVELS, STOCK_TYPES, User } from 'shared'
import { LISTING_AND_APPRAISAL, STOCK_DISPLAY } from 'utils/constants'

interface MobileActionsPresenterProps {
  user: User
  openActionsPresenterMobileDialog: boolean
  onPickAction: (value: INVENTORY_MULTI_SELECTION_UPDATES) => void
  onClose: () => void
  parentPage?: string
  handleOpenAutopriceDialog: () => void
  handleOpenPurchaseEventLinkingDialog: () => Promise<void>
  handleCloseDesktopActions: () => void
  setOpenMoveToTabDialog: () => void
  onDiscard: () => void
  onDelete: () => void
  editedStockExists: boolean
  activeStockType: string
  zoomLevel: string
}

export const MobileActionPresenterButton = React.memo(function MobileActionPresenterButton({
  label,
  iconClass,
  onClick,
  dataTestid,
  disabled = false,
  disabledTooltip,
}: {
  label: string
  iconClass: string
  onClick: () => void
  dataTestid: string
  disabled?: boolean
  disabledTooltip?: string
}) {
  return (
    <div
      className={`mobile-action-button${disabled ? ' disabled' : ''}`}
      onClick={disabled ? undefined : onClick}
      data-testid={dataTestid}
      title={disabled ? disabledTooltip : undefined}
      aria-disabled={disabled}
    >
      <span className="m-0 p-0 fs-6">{label}</span>
      <div className="icon-container">
        <i className={iconClass} />
      </div>
    </div>
  )
})

export const MobileActionsPresenter = React.memo(function MobileActionsPresenterDialogProps({
  user,
  openActionsPresenterMobileDialog,
  onPickAction,
  onClose,
  parentPage,
  handleOpenAutopriceDialog,
  handleCloseDesktopActions,
  handleOpenPurchaseEventLinkingDialog,
  setOpenMoveToTabDialog,
  onDiscard,
  onDelete,
  editedStockExists,
  activeStockType,
  zoomLevel,
}: MobileActionsPresenterProps): JSX.Element {
  const { t } = useTranslation()

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      className={`actions-overlay ${openActionsPresenterMobileDialog ? 'open' : ''}`}
    >
      <div className="actions-list d-flex flex-column justify-content-end align-items-end">
        {parentPage === LISTING_AND_APPRAISAL && (
          <MobileActionPresenterButton
            dataTestid="desktop-move-to-tab"
            label={t('stockPricing.bulkEdit.moveToOtherTab')}
            iconClass="fa-light fa-folder-arrow-down"
            onClick={() => {
              onClose()
              setOpenMoveToTabDialog()
            }}
          />
        )}

        {user.featureFlags?.taxation && (
          <>
            {user.enableMarginTaxation && (
              <MobileActionPresenterButton
                dataTestid="mobile-update-taxation-scheme"
                label={t('taxation.setTaxationScheme')}
                iconClass="fa-light fa-money-bill-transfer"
                onClick={() => {
                  onPickAction(INVENTORY_MULTI_SELECTION_UPDATES.NEW_TAXATION_SCHEME)
                }}
              />
            )}

            {parentPage === STOCK_DISPLAY && (
              <MobileActionPresenterButton
                dataTestid="mobile-associate-lot"
                label={t('taxation.associatePe')}
                iconClass="fa-light fa-boxes-stacked"
                onClick={() => {
                  handleCloseDesktopActions()
                  handleOpenPurchaseEventLinkingDialog()
                }}
              />
            )}
          </>
        )}

        <MobileActionPresenterButton
          dataTestid="mobile-set-price"
          label={t('stockPricing.bulkEdit.setPrice')}
          iconClass="fa-light fa-tag"
          onClick={() => onPickAction(INVENTORY_MULTI_SELECTION_UPDATES.NEW_PRICE)}
        />

        {parentPage !== LISTING_AND_APPRAISAL && (
          <MobileActionPresenterButton
            dataTestid="mobile-set-quantity"
            label={t('stockPricing.bulkEdit.setQuantity')}
            iconClass="fa-light fa-plus-minus"
            onClick={() => onPickAction(INVENTORY_MULTI_SELECTION_UPDATES.NEW_QUANTITY)}
            disabled={zoomLevel === INVENTORY_ZOOM_LEVELS.ARTICLE}
          />
        )}

        {activeStockType === STOCK_TYPES.SINGLES && (
          <MobileActionPresenterButton
            dataTestid="mobile-set-condition"
            label={t('stockPricing.bulkEdit.setCondition')}
            iconClass="fa-light fa-ranking-star"
            onClick={() => onPickAction(INVENTORY_MULTI_SELECTION_UPDATES.NEW_CONDITION)}
            disabled={zoomLevel === INVENTORY_ZOOM_LEVELS.ARTICLE}
          />
        )}

        <MobileActionPresenterButton
          dataTestid="mobile-set-location"
          label={t('stockPricing.bulkEdit.setLocation')}
          iconClass="fa-light fa-location-pen"
          onClick={() => onPickAction(INVENTORY_MULTI_SELECTION_UPDATES.NEW_LOCATION)}
          disabled={zoomLevel === INVENTORY_ZOOM_LEVELS.ARTICLE}
        />

        <MobileActionPresenterButton
          dataTestid="mobile-set-comment"
          label={t('stockPricing.bulkEdit.setComment')}
          iconClass="fa-light fa-message-dots"
          onClick={() => onPickAction(INVENTORY_MULTI_SELECTION_UPDATES.NEW_COMMENT)}
        />

        <MobileActionPresenterButton
          dataTestid="mobile-autoprice"
          label={t('stockPricing.actions.autopriceStock')}
          iconClass="fa-light fa-money-check-dollar-pen"
          onClick={() => {
            onClose()
            handleOpenAutopriceDialog()
          }}
        />

        {parentPage === LISTING_AND_APPRAISAL && (
          <MobileActionPresenterButton
            dataTestid="mobile-delete"
            label={t('stockPricing.actions.delete')}
            iconClass="fa-light fa-trash"
            onClick={() => {
              onClose()
              onDelete()
            }}
          />
        )}

        {editedStockExists && (
          <MobileActionPresenterButton
            dataTestid="mobile-discard"
            label={t('stockPricing.actions.discard')}
            iconClass="fa-light fa-arrow-rotate-left"
            onClick={() => {
              onClose()
              onDiscard()
            }}
          />
        )}

        {parentPage === LISTING_AND_APPRAISAL && (
          <MobileActionPresenterButton
            dataTestid="mobile-delete"
            label={t('stockPricing.actions.delete')}
            iconClass="fa-light fa-trash"
            onClick={() => {
              onClose()
              onDelete()
            }}
          />
        )}

        <div className={`floating-actions-presenter fake d-xl-none`} onClick={() => onClose()}>
          <i className={`fa-regular fa-xmark icon-close`} />
        </div>
      </div>
    </div>
  )
})
